// PhoneInput.tsx
import React, {
  useState,
  useRef,
  useLayoutEffect,
  KeyboardEvent,
  ClipboardEvent,
  MouseEvent,
  Fragment,
  useEffect,
} from 'react';
import './PhoneInput.css';

/* ------------------- Константы стран ------------------- */
type CountryCode = 'RU' | 'BY' | 'KZ';

interface CountryConfig {
  code: CountryCode;
  prefix: string;       // телефонный префикс (включает первый дефис)
  maxDigits: number;    // макс. количество цифр без кода страны
  placeholder: string;  // формат плейсхолдера
}

const COUNTRIES: Record<CountryCode, CountryConfig> = {
  RU: { code: 'RU', prefix: '+7 ', maxDigits: 10, placeholder: '+7 (___) ___-__-__' },
  BY: { code: 'BY', prefix: '+375-', maxDigits: 9, placeholder: '+375-__-___-__-__' },
  KZ: { code: 'KZ', prefix: '+7-', maxDigits: 10, placeholder: '+7-___-___-__-__' },
};

const COUNTRY_OPTIONS: { value: CountryCode; label: string }[] = [
  { value: 'RU', label: 'RU' },
  { value: 'BY', label: 'BY' },
  { value: 'KZ', label: 'KZ' },
];

/* ------------------- Утилиты ------------------- */

/**
 * Форматирует только «основную» часть номера (без префикса страны).
 * Группировка зависит от страны.
 */
const formatRest = (digits: string, maxDigits: number, country: CountryCode) => {
  const d = digits.padEnd(maxDigits, '_');

  if (country === 'RU') {
    // RU: (XXX) XXX-XX-XX (2-3-2-2 с пробелами и скобками)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
  }

  if (country === 'BY') {
    // BY: XX-XXX-XX-XX (2-3-2-2)
    return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5, 7)}-${d.slice(7, 9)}`;
  }

  // KZ: XXX-XXX-XX-XX (3-3-2-2)
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
};

/**
 * Переводит позицию caret (полный текст, включая префикс) → логический индекс
 */
const caretPosToDigitIndex = (formattedRest: string, visualCaret: number, prefixLength: number) => {
  const caretInRest = Math.max(0, visualCaret - prefixLength);
  let cnt = 0;
  for (let i = 0; i < caretInRest; i++) {
    if (/[0-9_]/.test(formattedRest[i])) cnt++;
  }
  return cnt;
};

/**
 * Переводит логический индекс → позицию caret в полном тексте
 */
const digitIndexToCaretPos = (formattedRest: string, digitIdx: number, prefixLength: number) => {
  let seen = 0;
  for (let i = 0; i < formattedRest.length; i++) {
    if (/[0-9_]/.test(formattedRest[i])) seen++;
    if (seen === digitIdx) return prefixLength + i + 1;
  }
  return prefixLength + formattedRest.length;
};

/* ------------------- Типы ------------------- */

type Props = {
  /** Выбранная код страны */
  country?: CountryCode;
  /** Изменение страны */
  onCountryChange?: (country: CountryCode) => void;
  /** Чистый буфер (только цифры, без кода страны) */
  value?: string;
  /** Callback каждый раз, когда буфер меняется */
  onChange?: (rawDigits: string) => void;
  /** Полный номер телефона (с префиксом) — альтернатива value */
  fullPhone?: string;
  /** Блокировать ввод */
  disabled?: boolean;
  /** CSS‑класс */
  className?: string;
  /** Плейсхолдер */
  placeholder?: string;
  /** Показывать селект страны */
  showCountrySelector?: boolean;
};

/* ------------------- Компонент ------------------- */

const PhoneInput: React.FC<Props> = ({
  country: propCountry = 'RU',
  onCountryChange,
  value,
  onChange,
  fullPhone,
  disabled = false,
  className,
  placeholder,
  showCountrySelector = true,
}) => {
  /* ---------- Состояния ---------- */
  const [country, setCountry] = useState<CountryCode>(propCountry);
  const isControlled = typeof value === 'string';
  const [internalDigits, setInternalDigits] = useState<string>('');
  const digits = isControlled ? value! : internalDigits;

  const config = COUNTRIES[country];
  const maxDigits = config.maxDigits;
  const prefix = config.prefix;
  const prefixLength = prefix.length;

  const inputPlaceholder = placeholder || config.placeholder;

  // Логический курсор
  const [cursor, setCursor] = useState<number>(0);

  // Состояние для dropdown селектора страны
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Синхронизация страны из пропа (страна управляется списком «Страна» в форме)
  useEffect(() => {
    setCountry(propCountry);
    setCursor(0);
  }, [propCountry]);

  // Закрытие dropdown при клике вне компонента
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = () => setIsOpen(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  /* ---------- Смена страны ---------- */
  const handleCountryChange = (newCountry: CountryCode) => {
    setCountry(newCountry);
    setCursor(0);
    setInternalDigits('');
    onChange?.('');
    onCountryChange?.(newCountry);
  };

  /* ---------- Синхронизация caret ---------------- */
  useLayoutEffect(() => {
    const formattedRest = formatRest(digits, maxDigits, country);
    const visualPos = digitIndexToCaretPos(formattedRest, cursor, prefixLength);
    if (inputRef.current) {
      inputRef.current.setSelectionRange(visualPos, visualPos);
    }
  }, [digits, cursor, maxDigits, prefixLength, country]);

  /* ---------- Обработчики клавиш --------------- */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const formattedRest = formatRest(digits, maxDigits, country);
    const input = inputRef.current!;
    const visualCaret = input.selectionStart ?? 0;
    const visualAnchor = input.selectionEnd ?? 0;

    // --------------------- Стрелки -----------------
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
      setCursor(Math.max(0, logicalIdx - 1));
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
      setCursor(Math.min(digits.length, logicalIdx + 1));
      return;
    }

    // --------------------- Home / End -----------------
    if (e.key === 'Home') {
      e.preventDefault();
      setCursor(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setCursor(digits.length);
      return;
    }

    // --------------------- Цифры -----------------
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const newChar = e.key;

      // 1️⃣ Выделен диапазон → заменяем его одной цифрой
      if (visualCaret !== visualAnchor) {
        const startIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
        const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor, prefixLength);
        let newDigits = digits.slice(0, startIdx) + newChar + digits.slice(endIdx);
        if (newDigits.length > maxDigits) newDigits = newDigits.slice(0, maxDigits);
        const newCursor = Math.min(startIdx + 1, maxDigits);

        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(newCursor);
        return;
      }

      // 2️⃣ Нет выделения – курсор внутри строки
      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);

      let newDigits: string;
      let newCursor: number;

      if (logicalIdx < digits.length) {
        newDigits = digits.slice(0, logicalIdx) + newChar + digits.slice(logicalIdx + 1);
        newCursor = logicalIdx + 1;
      } else {
        if (digits.length < maxDigits) {
          newDigits = digits + newChar;
          newCursor = digits.length + 1;
        } else {
          newDigits = digits;
          newCursor = digits.length;
        }
      }

      if (newDigits.length > maxDigits) newDigits = newDigits.slice(0, maxDigits);
      newCursor = Math.min(newCursor, maxDigits);

      if (!isControlled) setInternalDigits(newDigits);
      onChange?.(newDigits);
      setCursor(newCursor);
      return;
    }

    // --------------------- Backspace / Delete -----------------
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (visualCaret !== visualAnchor) {
        const startIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
        const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor, prefixLength);
        const newDigits = digits.slice(0, startIdx) + digits.slice(endIdx);
        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(startIdx);
        return;
      }

      if (cursor === 0) return;
      const newDigits = digits.slice(0, cursor - 1) + digits.slice(cursor);
      if (!isControlled) setInternalDigits(newDigits);
      onChange?.(newDigits);
      setCursor(cursor - 1);
      return;
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      if (visualCaret !== visualAnchor) {
        const startIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
        const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor, prefixLength);
        const newDigits = digits.slice(0, startIdx) + digits.slice(endIdx);
        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(startIdx);
        return;
      }

      const logicalIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
      if (logicalIdx < digits.length) {
        const newDigits = digits.slice(0, logicalIdx) + '_' + digits.slice(logicalIdx + 1);
        if (!isControlled) setInternalDigits(newDigits);
        onChange?.(newDigits);
        setCursor(logicalIdx);
      }
      return;
    }
  };

  /* ------------------- Паста (режим замены) ------------------- */
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const raw = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (!raw) return;

    const input = inputRef.current!;
    const visualCaret = input.selectionStart ?? prefixLength;
    const visualAnchor = input.selectionEnd ?? visualCaret;

    const formattedRest = formatRest(digits, maxDigits, country);
    const startIdx = caretPosToDigitIndex(formattedRest, visualCaret, prefixLength);
    const endIdx = caretPosToDigitIndex(formattedRest, visualAnchor, prefixLength);

    let newDigits: string;
    let newCursor: number;

    if (visualCaret !== visualAnchor) {
      newDigits = digits.slice(0, startIdx) + raw + digits.slice(endIdx);
      newCursor = startIdx + raw.length;
    } else {
      if (startIdx < digits.length) {
        const left = digits.slice(0, startIdx);
        const right = digits.slice(startIdx + raw.length);
        newDigits = left + raw + right;
        newCursor = startIdx + raw.length;
      } else {
        const free = maxDigits - digits.length;
        const toInsert = raw.slice(0, free);
        newDigits = digits + toInsert;
        newCursor = digits.length + toInsert.length;
      }
    }

    if (newDigits.length > maxDigits) newDigits = newDigits.slice(0, maxDigits);
    newCursor = Math.min(newCursor, maxDigits);

    if (!isControlled) setInternalDigits(newDigits);
    onChange?.(newDigits);
    setCursor(newCursor);
  };

  /* ------------------- Клик ------------------- */
  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    const visualPos = (e.target as HTMLInputElement).selectionStart ?? prefixLength;
    const formattedRest = formatRest(digits, maxDigits, country);
    const logicalIdx = caretPosToDigitIndex(formattedRest, visualPos, prefixLength);
    setCursor(logicalIdx);
  };

  const handleBlur = () => {
    // placeholder для будущей валидации
  };

  /* ------------------- Render ------------------- */
  const displayValue = fullPhone !== undefined && fullPhone !== ''
    ? fullPhone + formatRest(digits, maxDigits, country)
    : digits !== ''
      ? prefix + formatRest(digits, maxDigits, country)
      : '';

  return (
    <div className={`phone-input-wrapper${disabled ? ' disabled' : ''}`}>
      {/* Селект страны */}
      {showCountrySelector && (
        <div className="country-selector">
          <div
            className={`country-selector__trigger${disabled ? ' disabled' : ''}`}
            onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
          >
            <span className="country-selector__label">{country}</span>
            <svg className={`country-selector__arrow${isOpen ? ' open' : ''}`} width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          {isOpen && (
            <div className="country-selector__dropdown">
              {COUNTRY_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`country-selector__option${country === opt.value ? ' selected' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleCountryChange(opt.value); setIsOpen(false); }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Поле ввода телефона */}
      <input
        ref={inputRef}
        type="tel"
        className={`phone-input${className ? ' ' + className : ''}`}
        placeholder={inputPlaceholder}
        disabled={disabled}
        value={displayValue}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={handleClick}
        onBlur={handleBlur}
        inputMode="numeric"
        autoComplete="tel"
      />
    </div>
  );
};

export default PhoneInput;
export type { CountryCode };
